import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, FolderOpen, Pencil, Plus, RotateCcw, ArrowDown, ArrowUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ListRow } from '@/components/ui/Card';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard, SettingsEmpty, SettingsLoading, SettingsStack } from './settingsChrome';
import { HighlightTagsSettings } from './HighlightTagsSettings';
import {
  useCreatePatientFileCategory,
  usePatientFileCategories,
  useReorderPatientFileCategories,
  useUpdatePatientFileCategory,
  type PatientFileCategoryRow,
} from '@/hooks/usePatientFileCategories';
import { FileCategoryChip } from '@/components/documents/FileCategoryChip';
import {
  PATIENT_FILE_CATEGORY_COLOR_TOKENS,
  fileCategorySwatchClass,
  type PatientFileCategoryColorToken,
} from '@/lib/patientFileCategories';

export function PatientFileCategories() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const canManage = Boolean(activeClinicId && hasPermission(activeClinicId, 'documents', 'update'));

  const categoriesQuery = usePatientFileCategories(activeClinicId);
  const create = useCreatePatientFileCategory(activeClinicId);
  const update = useUpdatePatientFileCategory(activeClinicId);
  const reorder = useReorderPatientFileCategories(activeClinicId);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PatientFileCategoryRow | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState<PatientFileCategoryColorToken>('teal');

  const categories = categoriesQuery.data ?? [];
  const active = categories
    .filter((row) => !row.is_archived)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'da'));
  const archived = categories
    .filter((row) => row.is_archived)
    .sort((a, b) => a.name.localeCompare(b.name, 'da'));

  const openCreate = () => {
    setEditing(null);
    setName('');
    setColor('teal');
    setShowForm(true);
  };

  const openEdit = (row: PatientFileCategoryRow) => {
    setEditing(row);
    setName(row.name);
    setColor(row.color_token as PatientFileCategoryColorToken);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setName('');
    setColor('teal');
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          name,
          color_token: color,
        });
        toast.success(t('settings.file_categories.toast_updated'));
      } else {
        await create.mutateAsync({ name, color_token: color });
        toast.success(t('settings.file_categories.toast_created'));
      }
      closeForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings.file_categories.toast_error'));
    }
  };

  const move = async (row: PatientFileCategoryRow, direction: -1 | 1) => {
    const index = active.findIndex((item) => item.id === row.id);
    const swap = active[index + direction];
    if (!swap) return;
    try {
      await reorder.mutateAsync([
        { id: row.id, sort_order: swap.sort_order },
        { id: swap.id, sort_order: row.sort_order },
      ]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings.file_categories.toast_error'));
    }
  };

  const setArchived = async (row: PatientFileCategoryRow, isArchived: boolean) => {
    try {
      await update.mutateAsync({ id: row.id, is_archived: isArchived });
      toast.success(
        isArchived
          ? t('settings.file_categories.toast_archived')
          : t('settings.file_categories.toast_restored'),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('settings.file_categories.toast_error'));
    }
  };

  return (
    <SettingsStack>
      <SectionCard
        title={t('settings.file_categories.title')}
        subtitle={t('settings.file_categories.subtitle')}
        readOnly={!canManage}
        action={
          canManage && active.length > 0 ? (
            <Button onClick={openCreate} className="w-full nav:w-auto">
              <Plus className="h-4 w-4" aria-hidden />
              {t('settings.file_categories.new')}
            </Button>
          ) : undefined
        }
      >
        {categoriesQuery.isLoading ? (
          <SettingsLoading />
        ) : active.length === 0 && archived.length === 0 ? (
          <SettingsEmpty
            icon={<FolderOpen className="h-7 w-7" />}
            title={t('settings.file_categories.empty')}
            action={
              canManage ? (
                <Button onClick={openCreate} className="w-full">
                  <Plus className="h-4 w-4" aria-hidden />
                  {t('settings.file_categories.new')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="-mx-s4 nav:-mx-s5">
            {active.map((row, index) => (
              <ListRow key={row.id} className="justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-s3">
                  <FileCategoryChip name={row.name} colorToken={row.color_token} />
                </div>
                {canManage ? (
                  <div className="flex shrink-0 gap-s1">
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      disabled={index === 0 || reorder.isPending}
                      onClick={() => void move(row, -1)}
                      aria-label={t('settings.file_categories.move_up')}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      disabled={index === active.length - 1 || reorder.isPending}
                      onClick={() => void move(row, 1)}
                      aria-label={t('settings.file_categories.move_down')}
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      onClick={() => openEdit(row)}
                      aria-label={t('common.edit')}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      onClick={() => void setArchived(row, true)}
                      aria-label={t('settings.file_categories.archive')}
                    >
                      <Archive />
                    </Button>
                  </div>
                ) : null}
              </ListRow>
            ))}
          </div>
        )}

        {archived.length > 0 ? (
          <div className="mt-s5">
            <h3 className={cn(TYPE.label, 'mb-s2 text-ds-muted')}>
              {t('settings.file_categories.archived')}
            </h3>
            <div className="-mx-s4 nav:-mx-s5">
              {archived.map((row) => (
                <ListRow key={row.id} className="justify-between">
                  <FileCategoryChip name={row.name} colorToken={row.color_token} />
                  {canManage ? (
                    <Button
                      type="button"
                      variant="tertiary"
                      size="icon"
                      onClick={() => void setArchived(row, false)}
                      aria-label={t('settings.file_categories.restore')}
                    >
                      <RotateCcw />
                    </Button>
                  ) : null}
                </ListRow>
              ))}
            </div>
          </div>
        ) : null}

        {showForm ? (
          <Modal
            title={editing ? t('settings.file_categories.edit') : t('settings.file_categories.new')}
            onClose={closeForm}
          >
            <form onSubmit={(event) => void onSubmit(event)} className="space-y-s4">
              <Input
                label={t('settings.file_categories.field_name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={80}
              />
              <fieldset>
                <legend className={cn(TYPE.label, 'mb-s2 text-ds-text')}>
                  {t('settings.file_categories.field_color')}
                </legend>
                <div className="flex flex-wrap gap-s2">
                  {PATIENT_FILE_CATEGORY_COLOR_TOKENS.map((token) => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => setColor(token)}
                      aria-pressed={color === token}
                      aria-label={t(`settings.file_categories.colors.${token}`)}
                      className={cn(
                        'flex h-10 items-center gap-s2 rounded-ctl border px-s3',
                        color === token
                          ? 'border-ds-teal bg-ds-teal-tint'
                          : 'border-ds-field bg-ds-card',
                      )}
                    >
                      <span
                        className={cn('h-3.5 w-3.5 rounded-full', fileCategorySwatchClass(token))}
                        aria-hidden
                      />
                      <span className={cn(TYPE.control, 'text-ds-text')}>
                        {t(`settings.file_categories.colors.${token}`)}
                      </span>
                    </button>
                  ))}
                </div>
              </fieldset>
              <div className="flex justify-end gap-s2 pt-s2">
                <Button type="button" variant="secondary" onClick={closeForm}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={create.isPending || update.isPending}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Modal>
        ) : null}
      </SectionCard>
      <HighlightTagsSettings />
    </SettingsStack>
  );
}
